import type { SupportedLocale } from "#/lib/locale";
import { ApiError, apiFetch } from "./api";

/**
 * Public learn posts (`/v1/posts`) — published-only, snake_case, language
 * resolved server-side from the Accept-Language header (en fallback).
 * `category` is the learn section slug; ordering is newest-first and stable,
 * which the migration seeded to match the learn display order.
 */
export interface Post {
	id: string;
	title: string;
	slug: string;
	language: string;
	category: string;
	/** 섹션(카테고리) 내 표시 순서, 오름차순. 0은 미지정(최신순 폴백). */
	sort_order: number;
	body: string;
	created_at: string;
	updated_at: string;
}

interface PostPage {
	posts: Post[];
	next_cursor: string | null;
}

export async function fetchPosts(locale: SupportedLocale): Promise<Post[]> {
	// ponytail: full cursor walk (limit=50) — one request today (22 posts/lang)
	const posts: Post[] = [];
	let cursor: string | null = null;
	do {
		const page: PostPage = await apiFetch<PostPage>(
			`/v1/posts?limit=50${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
			{ headers: { "Accept-Language": locale } },
		);
		posts.push(...page.posts);
		cursor = page.next_cursor;
	} while (cursor);
	return posts;
}

/** Unknown slugs and drafts are an expected render state — 404 maps to null. */
export async function fetchPost(
	slug: string,
	locale: SupportedLocale,
): Promise<Post | null> {
	try {
		return await apiFetch<Post>(`/v1/posts/${encodeURIComponent(slug)}`, {
			headers: { "Accept-Language": locale },
		});
	} catch (error) {
		if (error instanceof ApiError && error.status === 404) {
			return null;
		}
		throw error;
	}
}
